import React, { Fragment, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom'
import { Nav, Navbar, NavbarBrand, Collapse, ButtonGroup, Label } from 'reactstrap';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { logOut, hideDarkTheme, showDarkTheme, resetDismissedHelps } from '../../../../redux';
import { Utils } from '../../../../services';
import { readSpoofOriginalUser } from '../../../../services/spoofSession';
import { Ability, QuickAccess, HealthCheckIcon, TktSyncButton } from '../../../../components';
import { canShowSyncImportPanel } from '../../../../services';
import i18n from '../../../../locales/i18n';
import logo from '../../../../assets/images/Kronos.svg';
import './TopBar.scss';
import CommandPaletteSearchTrigger from '../CommandPaletteSearchTrigger';

const TopBar = ({ section, onSectionSelected, onCollapseNavClick, onToggleSideBar }) => {
    const { user }          = useSelector(state => state.auth);
    const { isMobile, collapsedSideBar } = useSelector(state => state.ui);
    const { KUserSettings } = useSelector(state => state.userSettings);
    const { t }             = useTranslation();
    const dispatch          = useDispatch();
    const current_lng       = Utils.i18nUiLang(i18n);
    const show_sync_import  = canShowSyncImportPanel(user);

    const checkTopBarAbilities = useCallback((bar) => {
        if (!user) {
            return false;
        }
        return user.verifyAccessSections(bar);
    }, [user]);

    const availableSections = React.useMemo(() => {
        return Utils.appSections().filter((s => checkTopBarAbilities(s.id)));
    }, [checkTopBarAbilities]);

    if (!user)
        return null;

    const spoofOriginal = readSpoofOriginalUser();
    const logoutLabel = spoofOriginal
        ? t('app.reconnect_as', {
            name: spoofOriginal.firstname || spoofOriginal.fullname || spoofOriginal.email,
        })
        : t('app.logout');

    const doLogOut = () => {
        dispatch(logOut());
    };

    const toggleTheme = () => {
        if (KUserSettings.darkTheme === true)
            dispatch(hideDarkTheme());
        else
            dispatch(showDarkTheme());
    };

    const dismissed_help_ids = Object.keys(KUserSettings?.dismissedHelps || {})
        .filter((id) => KUserSettings.dismissedHelps[id] === true);
    const has_dismissed_helps = dismissed_help_ids.length > 0;

    const changeLanguage = lng => {
        i18n.changeLanguage(lng);
    };

    const onSelectSection = section => {
        onSectionSelected && onSectionSelected(section);
    };

    return (
        <Navbar className="TopBar fixed-top flex-md-nowrap p-0 shadow" expand="md">
            <div className={`TopBar__brand${collapsedSideBar && !isMobile ? ' TopBar__brand--collapsed' : ''}`}>
                <NavbarBrand href="/" className="navbar-brand">
                    <img
                        src="/favicon.ico"
                        className="logo logo--mark"
                        alt="Kronos"
                    />
                    <img
                        src={logo}
                        className="logo logo--full"
                        alt="Kronos"
                    />
                </NavbarBrand>
                {onToggleSideBar && !isMobile && (
                    <button
                        type="button"
                        className="TopBar__collapseBtn"
                        onClick={onToggleSideBar}
                        title={collapsedSideBar ? t('app.show_sidebar') : t('app.hide_sidebar')}
                        aria-label={collapsedSideBar ? t('app.show_sidebar') : t('app.hide_sidebar')}
                        aria-expanded={!collapsedSideBar}
                    >
                        <i
                            className={`fa fa-chevron-${collapsedSideBar ? 'right' : 'left'}`}
                            aria-hidden="true"
                        />
                    </button>
                )}
            </div>
            <Collapse navbar>
                <div className="TopBarMain">
                    <div className="d-none d-md-flex align-items-stretch flex-shrink-0 TopBar__search">
                        <CommandPaletteSearchTrigger />
                        <div className="topbar-search-separator" />
                    </div>
                    {availableSections?.length > 1 && (
                        <ButtonGroup className="topBarButtonGroup">
                            {availableSections?.map(s => (
                                <Link
                                    key={s.id}
                                    to={`/${s.id}`}
                                    className={`d-flex justify-content-center align-items-center topBarBtn btn ${section === s.id ? "btnActive" : "btnNotActive"}`}
                                    role="button"
                                    onClick={(e) => {
                                        // Mobile: only switch the sidebar section, keep current page.
                                        if (isMobile) {
                                            e.preventDefault();
                                        }
                                        onSelectSection(s.id);
                                    }}
                                >
                                    <i className={`fa ${s.icon} block`}></i>
                                    <span className='d-none d-md-inline'>&nbsp;{t(`app.section_${s.id}`)}</span>
                                </Link>
                            ))}
                        </ButtonGroup>
                    )}
                </div>
                <Nav className="ml-auto settingsNav" navbar>
                    {isMobile && show_sync_import && (
                        <div className="TopBar__syncImport">
                            <TktSyncButton collapsed />
                        </div>
                    )}
                    <Ability can="logs:readOther">
                        <div className={isMobile ? 'TopBar__healthCheck' : 'mx-2 d-flex TopBar__healthCheck'}>
                            <HealthCheckIcon />
                        </div>
                    </Ability>
                    <QuickAccess />
                    <UncontrolledDropdown nav inNavbar direction={isMobile ? 'up' : 'down'}>
                        <DropdownToggle nav caret aria-label={t(`languages.${current_lng}`)}>
                            <i className="fa fa-globe"></i>
                            <span className="TopBar__settingsLabel">&nbsp;{t(`languages.${current_lng}`)}</span>
                        </DropdownToggle>
                        <DropdownMenu right>
                            {['en', 'fr', 'de', 'it'].map((lng, i) =>
                                <Fragment key={i}>
                                    <DropdownItem key={lng} className={current_lng === lng ? 'active' : ''} onClick={() => {changeLanguage(lng); onCollapseNavClick()}}>
                                        {t(`languages.${lng}`)}
                                    </DropdownItem>
                                </Fragment>
                            )}
                        </DropdownMenu>
                    </UncontrolledDropdown>
                    <UncontrolledDropdown nav inNavbar direction={isMobile ? 'up' : 'down'}>
                        <DropdownToggle nav caret aria-label={user.firstname}>
                            <i className="fa fa-user"></i>
                            <span className="TopBar__settingsLabel">&nbsp;{user.firstname}</span>
                        </DropdownToggle>
                        <DropdownMenu right>
                            <DropdownItem
                                className={KUserSettings?.darkTheme ? 'active' : ''}
                                onClick={toggleTheme}
                                toggle={false}
                            >
                                <i className="fa fa-moon-o"></i>
                                <Label className="mb-0">&nbsp;{t("settings.dark_theme")}</Label>
                            </DropdownItem>
                            {has_dismissed_helps && (
                                <DropdownItem
                                    onClick={() => dispatch(resetDismissedHelps())}
                                    toggle={false}
                                >
                                    <i className="fa fa-question-circle"></i>
                                    <Label className="mb-0">&nbsp;{t("settings.reset_dismissed_helps")}</Label>
                                </DropdownItem>
                            )}
                            <DropdownItem divider />
                            <DropdownItem tag={Link} to={`/users/show/${user.id}`} onClick={onCollapseNavClick} className="text-dark">
                                <i className="fa fa-user"></i>&nbsp; {t('app.my_profil')}
                            </DropdownItem>
                            <DropdownItem divider />
                            <DropdownItem onClick={doLogOut}>
                                <i className="fa fa-sign-out"></i>&nbsp; {logoutLabel}
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </Nav>
            </Collapse>
        </Navbar>
    );
};

export default TopBar;
